import React from "react";
import { AlertOctagon, RefreshCw, Home } from "lucide-react";

/**
 * Root-level error boundary. Renders a branded 500 page when any descendant
 * throws during render. We don't try to recover automatically — the user
 * either retries or goes home.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || "Something went wrong" };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen bg-fc-navy text-white flex items-center justify-center px-6" data-testid="error-boundary-page">
        <div className="max-w-md text-center">
          <AlertOctagon className="mx-auto text-fc-red mb-4" size={48} />
          <p className="fc-overline text-fc-red mb-3">SOMETHING BROKE</p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight uppercase mb-3">
            Hit a snag.
          </h1>
          <p className="text-white/65 text-sm mb-6">
            We've logged the error. Retry, or head home and we'll pick up from there.
          </p>
          {process.env.NODE_ENV !== "production" && (
            <pre className="text-[10px] text-white/35 mb-6 text-left whitespace-pre-wrap break-words bg-black/30 p-3">
              {this.state.message}
            </pre>
          )}
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="btn-fc-red"
              data-testid="error-retry"
            >
              <RefreshCw size={14} /> Retry
            </button>
            <button
              onClick={() => { window.location.href = "/"; }}
              className="btn-fc-outline"
              data-testid="error-home"
            >
              <Home size={14} /> Home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
